import { LearningModule } from '../types';

export const LEARNING_MODULES: LearningModule[] = [
  {
    id: 'module-1-foundations',
    name: 'Module 1: SEO Foundations & Orientation',
    type: 'learning',
    version: '1.0',
    description: 'Master core SEO definitions, search engine mechanics, guidelines, search query concepts, SEO types, and the SEO audit health check analogy based on Lectures 1 and 2.',
    lectureSources: ['lec1-SEO Basics(3).vtt', 'lec2-SEO Basics(1).txt'],
    lessons: [
      {
        id: 'lesson-1-1',
        moduleId: 'module-1-foundations',
        lessonNumber: 1,
        title: 'What is SEO? (Search Engine + Optimization)',
        lecture: 1,
        sourceFile: 'lec1-SEO Basics(3).vtt',
        topic: 'SEO Definition',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand what the acronym SEO stands for and how its two root words work together.',
        instructorExplanation: 'SEO is the short form of Search Engine Optimization. The instructor explains that this phrase is created by joining two core concepts: "Search Engine" and "Optimization". Optimization literally means updation or updating. Therefore, SEO means updating a website according to the specific guidelines of a search engine so it can easily understand, crawl, index, and rank the site.',
        instructorExamples: [
          'Searching on Google for "best SEO training"',
          'Searching for "best HP laptop"'
        ],
        simpleExplanation: 'SEO = Search Engine + Optimization (updating your site following search engine rules).',
        comprehensionCheck: {
          id: 'check-1-1',
          question: 'According to the instructor, what is the literal meaning of "Optimization" in SEO?',
          options: [
            { label: 'Buying paid advertisement spots on search result pages', value: 'a', isCorrect: false },
            { label: 'Updating or updation of a website following search engine guidelines', value: 'b', isCorrect: true },
            { label: 'Writing complex coding scripts for backend databases', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor explicitly teaches that Optimization means "updation" — updating your website according to search engine guidelines.'
        }
      },
      {
        id: 'lesson-1-2',
        moduleId: 'module-1-foundations',
        lessonNumber: 2,
        title: 'Search Engines & How They Work',
        lecture: 1,
        sourceFile: 'lec1-SEO Basics(3).vtt',
        topic: 'Search Engine Platforms',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn what constitutes a search engine and identify major search engine platforms.',
        instructorExplanation: 'A Search Engine is any platform or tool that helps a user search for information on the internet. When a user wants to find a product, service, or educational course, they enter a query into a search engine bar to get relevant results.',
        instructorExamples: [
          'Google (google.com)',
          'Yahoo',
          'Bing',
          'AOL',
          'Altavista',
          'Yandex',
          'Baidu'
        ],
        simpleExplanation: 'Search engines are tools that help users discover relevant content on the internet.',
        comprehensionCheck: {
          id: 'check-1-2',
          question: 'Which group of search engines was explicitly listed by the instructor?',
          options: [
            { label: 'Google, Yahoo, Bing, AOL, Altavista, Yandex, Baidu', value: 'a', isCorrect: true },
            { label: 'Chrome, Firefox, Safari, Edge, Opera', value: 'b', isCorrect: false },
            { label: 'WordPress, Shopify, Wix, Squarespace', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor specifically listed Google, Yahoo, Bing, AOL, Altavista, Yandex, and Baidu as search engine platforms.'
        }
      },
      {
        id: 'lesson-1-3',
        moduleId: 'module-1-foundations',
        lessonNumber: 3,
        title: 'Search Engine Guidelines & Ranking Factors',
        lecture: 1,
        sourceFile: 'lec1-SEO Basics(3).vtt',
        topic: 'Guidelines & Ranking Signals',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand why search engines issue guidelines and what factors influence rankings.',
        instructorExplanation: 'Every search engine maintains guidelines for website owners. Search engines want websites to be updated in a way that makes them easy to crawl, understand, and index. Key factors highlighted by the instructor include User Experience (UX), site loading speed, and content relevance to search queries.',
        instructorExamples: [
          'Site loading speed (fast loading pages rank better)',
          'Content relevance (matching exact user query intent)',
          'Mobile accessibility and user experience'
        ],
        simpleExplanation: 'Search engine guidelines ensure your website is fast, easy to crawl, and relevant to users.',
        comprehensionCheck: {
          id: 'check-1-3',
          question: 'What primary site quality factors did the instructor highlight as search engine ranking guidelines?',
          options: [
            { label: 'User experience, site loading speed, and content relevance', value: 'a', isCorrect: true },
            { label: 'Domain registration length and expensive server hosting', value: 'b', isCorrect: false },
            { label: 'Using dark background colors and heavy image animations', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor highlighted user experience, loading speed, and content relevance as core search engine guidelines.'
        }
      },
      {
        id: 'lesson-1-4',
        moduleId: 'module-1-foundations',
        lessonNumber: 4,
        title: 'Keywords vs. Search Queries',
        lecture: 1,
        sourceFile: 'lec1-SEO Basics(3).vtt',
        topic: 'Keywords Concept',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand what keywords and search queries are and how they connect searchers to websites.',
        instructorExplanation: 'If you break down the word "Keyword", it is the key word or phrase of your search. Any phrase or term entered into a search engine by a user attempting to find something is a search query or keyword.',
        instructorExamples: [
          '"best HP laptop for students"',
          '"what is the fee of NICE University"',
          '"best SEO course"'
        ],
        simpleExplanation: 'Keywords are the exact words or phrases users type into search engines when looking for answers.',
        comprehensionCheck: {
          id: 'check-1-4',
          question: 'How does the instructor define a "Keyword"?',
          options: [
            { label: 'A secret password required to access server files', value: 'a', isCorrect: false },
            { label: 'Any word or search query used on a search engine to search for information', value: 'b', isCorrect: true },
            { label: 'The license code used to activate software', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor defines a keyword as any word or search query used to find information on search engines.'
        }
      },
      {
        id: 'lesson-1-5',
        moduleId: 'module-1-foundations',
        lessonNumber: 5,
        title: 'Types of SEO Introduced',
        lecture: 1,
        sourceFile: 'lec1-SEO Basics(3).vtt',
        topic: 'SEO Categorizations',
        coverage: 'explicit',
        whatAreWeLearning: 'Explore the different branches of SEO introduced by the instructor.',
        instructorExplanation: 'The instructor introduces several types of SEO based on scope and platform:\n• Global / International SEO: Ranking a website or business worldwide without location restrictions (e.g. Amazon).\n• Local SEO: Ranking a business within a specific target city or geographic area (e.g. Multan).\n• Technical SEO, Shopify SEO, and YouTube SEO.',
        instructorExamples: [
          'Global SEO: Amazon operating worldwide',
          'Local SEO: A business targeting Multan city',
          'Platform SEO: YouTube SEO, Shopify SEO'
        ],
        simpleExplanation: 'SEO can target the entire world (Global SEO), a specific city (Local SEO), or platforms like YouTube.',
        comprehensionCheck: {
          id: 'check-1-5',
          question: 'What is the main distinction between Global SEO and Local SEO as taught by the instructor?',
          options: [
            { label: 'Global SEO targets worldwide audiences without location limits, while Local SEO targets a specific location/city', value: 'a', isCorrect: true },
            { label: 'Global SEO is free, while Local SEO requires paid advertising fees', value: 'b', isCorrect: false },
            { label: 'Global SEO is only for videos, while Local SEO is only for PDF files', value: 'c', isCorrect: false }
          ],
          explanation: 'Global SEO ranks websites worldwide, whereas Local SEO focuses on ranking within a specific target area or city.'
        }
      },
      {
        id: 'lesson-1-6',
        moduleId: 'module-1-foundations',
        lessonNumber: 6,
        title: 'What is an SEO Audit? (The Doctor Checkup Analogy)',
        lecture: 2,
        sourceFile: 'lec2-SEO Basics(1).txt',
        topic: 'SEO Audit Concept & Analogy',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand what an SEO Audit is using the instructor\'s doctor health checkup analogy.',
        instructorExplanation: 'An SEO Audit is the first part of any SEO project. The instructor uses the analogy of visiting a doctor for a general health checkup. The doctor conducts a complete examination, runs blood tests, and evaluates your body to check if any hidden issues or illnesses exist. Similarly, an SEO Audit evaluates a website to check for errors, loading speed issues, broken links, image sizes, and keyword alignment.',
        instructorExamples: [
          'Doctor general checkup & blood test analogy',
          'Checking website loading speed and broken links',
          'Evaluating keyword alignment and content quality'
        ],
        simpleExplanation: 'An SEO Audit is a complete health checkup for a website to find errors before starting optimization.',
        comprehensionCheck: {
          id: 'check-1-6',
          question: 'What analogy does the instructor use to explain an SEO Audit?',
          options: [
            { label: 'Building a skyscraper foundation', value: 'a', isCorrect: false },
            { label: 'Visiting a doctor for a general health checkup and blood tests', value: 'b', isCorrect: true },
            { label: 'Tuning up a race car engine', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor compares an SEO audit to a doctor conducting a general health checkup on a patient.'
        }
      }
    ]
  },
  {
    id: 'module-2-local-intro',
    name: 'Module 2: Local SEO Introduction & Roadmap',
    type: 'learning',
    version: '1.0',
    description: 'Learn local business definitions, freelance earning potential, algorithm update immunity, 3 business types, 8 roadmap ingredients, 3 monetization models, and 10 common local site issues based on Lecture 3.',
    lectureSources: ['lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt'],
    lessons: [
      {
        id: 'lesson-2-1',
        moduleId: 'module-2-local-intro',
        lessonNumber: 1,
        title: 'What is a Local Business & Target Area?',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Local Business Concept',
        coverage: 'explicit',
        whatAreWeLearning: 'Define what a local business is and identify its target geographical area.',
        instructorExplanation: 'A Local Business is any business operating within a specific target area or city where its audience, patients, or clients reside. Because their physical service delivery is tied to that city, their marketing focuses on ranking locally.',
        instructorExamples: [
          'Dentists operating in Multan or Miami',
          'Plastic Surgeons & Law Firms in specific cities',
          'Electricians, Plumbers, Towing services, Limousine/Car rentals'
        ],
        simpleExplanation: 'A local business serves customers within a specific city or physical neighborhood.',
        comprehensionCheck: {
          id: 'check-2-1',
          question: 'How does the instructor define a Local Business?',
          options: [
            { label: 'Any business that sells products exclusively on Amazon', value: 'a', isCorrect: false },
            { label: 'A business operating in a specific target location/city where its customers reside', value: 'b', isCorrect: true },
            { label: 'A digital software company with no physical office', value: 'c', isCorrect: false }
          ],
          explanation: 'A local business operates in a specific target location/city where its local audience resides.'
        }
      },
      {
        id: 'lesson-2-2',
        moduleId: 'module-2-local-intro',
        lessonNumber: 2,
        title: 'Local SEO Scope & Freelance Earning Potential',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Marketplaces & Scope',
        coverage: 'explicit',
        whatAreWeLearning: 'Explore the high demand for Local SEO across marketplaces and realistic freelance earnings.',
        instructorExplanation: 'Millions of local businesses worldwide need marketing and SEO services to acquire clients online. On freelancing platforms like Upwork, over 90% of client SEO requests are for Local SEO. Beginners start at $200–$600/month per client, while experienced specialists earn $1,000–$3,000/month per account, scaling to $6,000–$10,000/month with multiple accounts.',
        instructorExamples: [
          'Upwork (90% of SEO requests are Local SEO)',
          'Fiverr, PeoplePerHour, Facebook Marketplace',
          '$200–$600/mo beginner retainers scaling to $1k–$3k/mo per client'
        ],
        simpleExplanation: 'Local SEO is in high demand worldwide, offering high monthly retainer income for freelancers.',
        comprehensionCheck: {
          id: 'check-2-2',
          question: 'What percentage of client SEO requests on Upwork pertain to Local SEO according to the instructor?',
          options: [
            { label: 'Around 20%', value: 'a', isCorrect: false },
            { label: 'Over 90%', value: 'b', isCorrect: true },
            { label: 'Exactly 50%', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor explicitly notes that around 90% of client SEO job postings on Upwork belong to Local SEO.'
        }
      },
      {
        id: 'lesson-2-3',
        moduleId: 'module-2-local-intro',
        lessonNumber: 3,
        title: 'Algorithm Update Stability in Local SEO',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Google Update Immunity',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand why Local SEO is far less vulnerable to Google algorithm updates than affiliate/blogging sites.',
        instructorExplanation: 'In blogging and affiliate marketing, Google constantly rolls out algorithm updates that cause traffic and earnings drops. In Local SEO, Google knows these websites represent real physical businesses operating in real locations. Google trusts real local businesses. Over 12 years of working in Local SEO, the instructor observed only 2 to 3 major Google updates.',
        instructorExamples: [
          'Blogging & Affiliate sites hit frequently by core updates',
          'Local real businesses enjoying long-term stable rankings',
          'Only 2–3 major updates in 12 years of Local SEO experience'
        ],
        simpleExplanation: 'Local SEO is stable and safe because Google trusts physical, real-world businesses.',
        comprehensionCheck: {
          id: 'check-2-3',
          question: 'Why is Local SEO resilient against frequent Google algorithm updates according to the instructor?',
          options: [
            { label: 'Because local websites do not use HTML code', value: 'a', isCorrect: false },
            { label: 'Because Google trusts real physical businesses operating in actual locations', value: 'b', isCorrect: true },
            { label: 'Because local businesses pay Google a monthly tax to avoid updates', value: 'c', isCorrect: false }
          ],
          explanation: 'Local SEO represents real physical businesses, earning search engine trust and algorithmic stability.'
        }
      },
      {
        id: 'lesson-2-4',
        moduleId: 'module-2-local-intro',
        lessonNumber: 4,
        title: 'Types of Local Businesses (Service, Product, Hybrid)',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Business Classifications',
        coverage: 'explicit',
        whatAreWeLearning: 'Classify local businesses into Service, Product, and Hybrid models.',
        instructorExplanation: 'The instructor categorizes local businesses into three distinct models:\n1. Service Businesses: Provide physical services (e.g. Electricians, Plumbers, Tree removal, Sketch artists).\n2. Product Businesses: Sell physical products locally (e.g. Toy brands, local gift shops).\n3. Hybrid Businesses: Offer services AND sell products (e.g. Dentists providing scaling/implants while selling braces/mouthwashes; Plastic Surgeons; Artificial grass installers selling grass materials and providing installation).',
        instructorExamples: [
          'Service: Plumbers, Electricians, Tree removal',
          'Product: Toy stores, local gift shops',
          'Hybrid: Dentists (services + braces/products), Plastic Surgeons'
        ],
        simpleExplanation: 'Local businesses provide services, sell products, or do both (Hybrid businesses).',
        comprehensionCheck: {
          id: 'check-2-4',
          question: 'What is a "Hybrid Business" as defined by the instructor?',
          options: [
            { label: 'A business that operates both online and on television', value: 'a', isCorrect: false },
            { label: 'A business that offers services AND sells physical products simultaneously (e.g. Dentists)', value: 'b', isCorrect: true },
            { label: 'A business owned by two different corporate partners', value: 'c', isCorrect: false }
          ],
          explanation: 'A hybrid business delivers professional services while also selling related physical products.'
        }
      },
      {
        id: 'lesson-2-5',
        moduleId: 'module-2-local-intro',
        lessonNumber: 5,
        title: 'The 8 Ingredients of Local SEO Roadmap',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Local SEO Ingredients & Roadmap',
        coverage: 'explicit',
        whatAreWeLearning: 'Identify the 8 core ingredients that form the instructor\'s Local SEO roadmap.',
        instructorExplanation: 'The instructor outlines the 8 essential ingredients required for Local SEO success:\n1. SEO Audit (Completed in Module 4 practical audit)\n2. Keyword Research\n3. Technicalities\n4. On-Page SEO\n5. Site Structure & Generation\n6. Content Planning & Writing\n7. Off-Page SEO\n8. GMB / Google My Business',
        instructorExamples: [
          'Step 1: SEO Audit (First prerequisite)',
          'Steps 2–8: Roadmap ingredients to be explored in future modules'
        ],
        simpleExplanation: 'Local SEO follows an 8-step roadmap starting with an SEO Audit and moving through keyword research, technicals, on-page, structure, content, off-page, and GMB.',
        futureNotice: 'Ingredients 2 through 8 are introduced here as roadmap topics and will be explored in future practical modules.',
        comprehensionCheck: {
          id: 'check-2-5',
          question: 'Which of the following is Ingredient #1 in the instructor\'s Local SEO roadmap?',
          options: [
            { label: 'Off-Page SEO Backlinks', value: 'a', isCorrect: false },
            { label: 'SEO Audit', value: 'b', isCorrect: true },
            { label: 'Google Ads PPC', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor places the SEO Audit as Ingredient #1 at the foundation of the Local SEO roadmap.'
        }
      },
      {
        id: 'lesson-2-6',
        moduleId: 'module-2-local-intro',
        lessonNumber: 6,
        title: 'Local SEO Monetization Models',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Monetization Models',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn the 3 distinct ways to monetize Local SEO skills.',
        instructorExplanation: 'The instructor details three proven monetization models:\n1. Client Services: Provide monthly Local SEO services directly to business owners for a recurring fee ($200–$2,000/mo).\n2. Pay Per Call / Pay Per Lead: Build and rank a niche service website (e.g. Tree Removal), then forward phone calls to local providers for $10–$50 per qualified call.\n3. Rank and Rent: Build and rank a local service website in a specific city, then rent out the entire website/leads to a local business for $200–$2,000/month.',
        instructorExamples: [
          'Client Services: Monthly retainers',
          'Pay Per Call: Tree removal site forwarding calls for $20/call',
          'Rank and Rent: Renting a ranked plumbing site for $500/mo'
        ],
        simpleExplanation: 'You can earn money by taking client retainers, selling calls/leads, or renting ranked websites.',
        comprehensionCheck: {
          id: 'check-2-6',
          question: 'What is the "Rank and Rent" monetization model as taught by the instructor?',
          options: [
            { label: 'Renting office space from a local business client', value: 'a', isCorrect: false },
            { label: 'Building and ranking a local service site, then renting the site/leads to a local business for a monthly fee', value: 'b', isCorrect: true },
            { label: 'Paying Google a monthly rent to stay on Page 1', value: 'c', isCorrect: false }
          ],
          explanation: 'Rank and Rent involves building and ranking a local website, then renting the website and lead flow to a local provider.'
        }
      },
      {
        id: 'lesson-2-7',
        moduleId: 'module-2-local-intro',
        lessonNumber: 7,
        title: 'Common Problems in Local SEO Sites',
        lecture: 3,
        sourceFile: 'lec3-Local SEO Intro, Outline, Scope, Monetization(1).txt',
        topic: 'Typical Client Site Defects',
        coverage: 'explicit',
        whatAreWeLearning: 'Identify common technical, structural, and content defects found on client websites.',
        instructorExplanation: 'Local business owners often hire web developers who build attractive sites but lack SEO knowledge. The instructor lists common issues found on client sites:\n• Low search volume (200–2,000 monthly searches, though lead value is high)\n• Thin content (only 300–400 words per page)\n• Missing Call To Action (CTA like "Call us to get a free quote")\n• Missing SSL (HTTP instead of HTTPS)\n• Slow loading speed & large image file sizes (>100 KB)\n• Missing pages & missing legal pages (Privacy Policy, Terms of Use, Disclaimer, Feedback)\n• Missing NAP (Name, Address, Phone number) & unoptimized GMB',
        instructorExamples: [
          'Developer building a pretty site with no content or SEO',
          'Site lacking SSL certificate (unsecure HTTP)',
          'Oversized images and missing Call-To-Action buttons'
        ],
        simpleExplanation: 'Client sites often suffer from thin content, missing CTAs, unsecure HTTP, slow speeds, and missing legal pages.',
        comprehensionCheck: {
          id: 'check-2-7',
          question: 'Which of the following is a common issue on local business sites highlighted by the instructor?',
          options: [
            { label: 'Thin content, missing CTAs, slow load speed, and missing legal pages', value: 'a', isCorrect: true },
            { label: 'Having too many high-authority backlinks from government sites', value: 'b', isCorrect: false },
            { label: 'Over-optimizing W3C code to 0 errors', value: 'c', isCorrect: false }
          ],
          explanation: 'Thin content, missing CTAs, unsecure HTTP, and missing legal pages are standard local site issues.'
        }
      }
    ]
  },
  {
    id: 'module-3-pillars-structure',
    name: 'Module 3: Local SEO Pillars & Site Structure',
    type: 'learning',
    version: '1.0',
    description: 'Master the 3 Pillars of Local SEO (Prominence, Proximity, Relevance), the divorce lawyer analogy, the crux definition, and local website structure rules based on Lecture 4.',
    lectureSources: ['lec4-Pillars Of Local SEO + Site Structure(1).txt'],
    lessons: [
      {
        id: 'lesson-3-1',
        moduleId: 'module-3-pillars-structure',
        lessonNumber: 1,
        title: 'The Three Pillars of Local SEO',
        lecture: 4,
        sourceFile: 'lec4-Pillars Of Local SEO + Site Structure(1).txt',
        topic: 'Pillars Framework',
        coverage: 'explicit',
        whatAreWeLearning: 'Master the three fundamental pillars of Local SEO: Prominence, Proximity, and Relevance.',
        instructorExplanation: 'Local SEO relies strictly on three core pillars:\n1. Prominence: How famous, established, and well-known a business is in its target area (Household brand awareness).\n2. Proximity: The physical geographic location and distance of the business matching the target area.\n3. Relevance: How accurately the business content and keywords match the target audience search intent.',
        instructorExamples: [
          'Prominence: Traditional flyers/pamphlets vs digital branding',
          'Proximity: Dentist operating in Multan city location',
          'Relevance: Keyword alignment matching target customer queries'
        ],
        simpleExplanation: 'The 3 pillars of Local SEO are Prominence (fame), Proximity (location), and Relevance (keyword match).',
        comprehensionCheck: {
          id: 'check-3-1',
          question: 'What are the Three Pillars of Local SEO as taught by the instructor?',
          options: [
            { label: 'Prominence, Proximity, and Relevance', value: 'a', isCorrect: true },
            { label: 'Speed, Backlinks, and Social Shares', value: 'b', isCorrect: false },
            { label: 'Domain, Hosting, and Design', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor explicitly defines the 3 pillars of Local SEO as Prominence, Proximity, and Relevance.'
        }
      },
      {
        id: 'lesson-3-2',
        moduleId: 'module-3-pillars-structure',
        lessonNumber: 2,
        title: 'Instructor Analogy for Relevance (The Divorce Lawyer Example)',
        lecture: 4,
        sourceFile: 'lec4-Pillars Of Local SEO + Site Structure(1).txt',
        topic: 'Relevance Principle & Analogy',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand the principle of Relevance using the instructor\'s divorce lawyer analogy.',
        instructorExplanation: 'Relevance means targeting the exact right audience with your marketing. The instructor uses the analogy of a divorce lawyer handing out business cards or flyers outside a primary school where young children are walking out. Young school kids are NOT the target audience for a divorce lawyer! Marketing to an irrelevant audience wastes money and effort. Keywords must capture the exact intent of your target customer.',
        instructorExamples: [
          'Divorce lawyer handing out cards to primary school kids (Irrelevant audience)',
          'Targeting dental service keywords to people seeking dental care in Multan (Relevant audience)'
        ],
        simpleExplanation: 'Relevance means showing your offer only to people who actually need your specific service.',
        comprehensionCheck: {
          id: 'check-3-2',
          question: 'What does the divorce lawyer primary school analogy demonstrate?',
          options: [
            { label: 'The importance of targeting an audience that is actually relevant to your service', value: 'a', isCorrect: true },
            { label: 'That primary schools are great places to advertise legal services', value: 'b', isCorrect: false },
            { label: 'That lawyer websites do not need keywords', value: 'c', isCorrect: false }
          ],
          explanation: 'The primary school lawyer analogy demonstrates that marketing to an irrelevant audience is ineffective.'
        }
      },
      {
        id: 'lesson-3-3',
        moduleId: 'module-3-pillars-structure',
        lessonNumber: 3,
        title: 'The Crux of Local SEO',
        lecture: 4,
        sourceFile: 'lec4-Pillars Of Local SEO + Site Structure(1).txt',
        topic: 'Core Local SEO Definition',
        coverage: 'explicit',
        whatAreWeLearning: 'Memorize the instructor\'s definitive core summary statement for Local SEO.',
        instructorExplanation: 'Combining Prominence, Proximity, and Relevance yields the instructor\'s core definition — the crux of Local SEO:\n"A business doing branding at a specific location among its targeted audience."',
        instructorExamples: [
          'Branding (Prominence) + Specific Location (Proximity) + Targeted Audience (Relevance)'
        ],
        simpleExplanation: 'Crux of Local SEO: "A business doing branding at a specific location among its targeted audience."',
        comprehensionCheck: {
          id: 'check-3-3',
          question: 'What is the exact "Crux of Local SEO" formula stated by the instructor?',
          options: [
            { label: 'A business doing branding at a specific location among its targeted audience', value: 'a', isCorrect: true },
            { label: 'Buying 1,000 backlinks from overseas websites', value: 'b', isCorrect: false },
            { label: 'Writing 50 blog posts every single day', value: 'c', isCorrect: false }
          ],
          explanation: 'The instructor defines the crux of Local SEO as: "A business doing branding at a specific location among its targeted audience."'
        }
      },
      {
        id: 'lesson-3-4',
        moduleId: 'module-3-pillars-structure',
        lessonNumber: 4,
        title: 'Local Business Website Structure',
        lecture: 4,
        sourceFile: 'lec4-Pillars Of Local SEO + Site Structure(1).txt',
        topic: 'Site Navigation & Page Architecture',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn the required page structure and navigation hierarchy for a local business website.',
        instructorExplanation: 'A proper Local SEO website requires a clear page architecture:\n• Homepage: Main landing page with business overview and NAP.\n• Service Pages: Dedicated individual subpages for every service offered (e.g. Dental Implants, Dental Braces, Dental Scaling, Root Canal).\n• Location Pages: Dedicated pages for each targeted city in multi-location businesses (e.g. Miami, Fort Lauderdale).\n• Blog: Educational articles for patients/clients.\n• Legal/Utility Footer Pages: Privacy Policy, Terms of Use, Disclaimer, Feedback page.',
        instructorExamples: [
          'Dentist site: Homepage, Dental Implants page, Dental Braces page, Root Canal page',
          'Footer: Privacy Policy, Terms of Use, Disclaimer, Feedback'
        ],
        simpleExplanation: 'A local site needs a homepage, dedicated pages for each individual service, location pages, a blog, and legal footer pages.',
        comprehensionCheck: {
          id: 'check-3-4',
          question: 'Why should a local business create separate dedicated pages for each service?',
          options: [
            { label: 'So each individual service page can be targeted and ranked for its specific keyword queries', value: 'a', isCorrect: true },
            { label: 'To make the website take longer to load', value: 'b', isCorrect: false },
            { label: 'Because Google penalizes websites with fewer than 100 pages', value: 'c', isCorrect: false }
          ],
          explanation: 'Dedicated service pages allow specific keywords and content to target each service query effectively.'
        }
      },
      {
        id: 'lesson-3-5',
        moduleId: 'module-3-pillars-structure',
        lessonNumber: 5,
        title: 'Single-Location vs. Multi-Location Businesses',
        lecture: 4,
        sourceFile: 'lec4-Pillars Of Local SEO + Site Structure(1).txt',
        topic: 'Location Structural Hierarchy',
        coverage: 'explicit',
        whatAreWeLearning: 'Distinguish between single-location and multi-location business website structures.',
        instructorExplanation: 'The instructor distinguishes site structures based on geographic reach:\n• Single-Location Business: Operates in one city (e.g. Multan). Does not require separate location pages.\n• Multi-Location Business: Operates across multiple cities (e.g. Miami, Fort Lauderdale). Requires dedicated location pages for each city to rank in those respective markets.',
        instructorExamples: [
          'Single-location clinic in Multan',
          'Multi-location business with branches in Miami and Fort Lauderdale'
        ],
        simpleExplanation: 'Single-location sites focus on one city; multi-location sites build dedicated pages for each target city.',
        futureNotice: 'Keyword research methodologies introduced in Lecture 4 will be explored in future practical modules.',
        comprehensionCheck: {
          id: 'check-3-5',
          question: 'When does a local business require dedicated location pages according to the instructor?',
          options: [
            { label: 'When it operates across multiple target cities (multi-location business)', value: 'a', isCorrect: true },
            { label: 'Only when it sells physical products internationally', value: 'b', isCorrect: false },
            { label: 'Every local business must have 10 location pages regardless of location', value: 'c', isCorrect: false }
          ],
          explanation: 'Multi-location businesses operating across multiple cities require dedicated location pages for each city.'
        }
      }
    ]
  },
  {
    id: 'module-4-onpage-seo',
    name: 'Module 4: On-Page SEO Implementation & URL/NAP Architecture',
    type: 'learning',
    version: '1.0',
    description: 'Master WordPress category setup, subpage and menu navigation hierarchy, single vs multi-location page optimization strategies, content length scaling by search volume & competition, permalinks and 301 redirection rules, and NAP consistency & Google Business Profile (GMB) trust signals based on Lecture 5.',
    lectureSources: ['lec5-On Page SEO Implementation.txt'],
    lessons: [
      {
        id: 'lesson-4-1',
        moduleId: 'module-4-onpage-seo',
        lessonNumber: 1,
        title: 'WordPress Categories, Subpages & Menu Navigation Structure',
        lecture: 5,
        sourceFile: 'lec5-On Page SEO Implementation.txt',
        topic: 'Category Architecture & 1-Click Away Rule',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn how to construct service categories, assign subpages, and configure menu navigation so pages remain maximum 1 click away.',
        instructorExplanation: 'The instructor demonstrates on a live WordPress demo site how to structure service pages and categories. First, create service categories under Dashboard -> Posts -> Categories (e.g., Demo Category). Next, navigate to Appearance -> Menus, select the category, and add it to the primary navigation menu. Then, create individual service pages (e.g., Test Page / Web Design) and assign them under their respective category. Ensuring every subpage is maximum 1 click away from navigation improves search engine crawlability and visitor user experience.',
        instructorExamples: [
          'Dashboard -> Posts -> Categories -> Add Category ("Demo / Services")',
          'Appearance -> Menus -> View All -> Add Category to Menu -> Save Menu',
          'Pages -> Add New -> "Test Page" -> Assign to Demo Category -> Save Menu -> Refresh'
        ],
        simpleExplanation: 'Create service categories, link them in main menus, and assign subpages so every page is 1-click away from navigation.',
        comprehensionCheck: {
          id: 'check-4-1',
          question: 'Why does the instructor emphasize organizing service pages under categories in menus so they are 1-click away?',
          options: [
            { label: 'To ensure search engines and users can discover and crawl pages easily without deep navigation paths', value: 'a', isCorrect: true },
            { label: 'To reduce server hosting costs by 50%', value: 'b', isCorrect: false },
            { label: 'Because WordPress automatically deletes pages that are 2 clicks away', value: 'c', isCorrect: false }
          ],
          explanation: 'Organizing subpages under categories so they are 1-click away enhances crawlability, indexability, and user accessibility.'
        }
      },
      {
        id: 'lesson-4-2',
        moduleId: 'module-4-onpage-seo',
        lessonNumber: 2,
        title: 'Single-Location vs. Multi-Location Page Strategy',
        lecture: 5,
        sourceFile: 'lec5-On Page SEO Implementation.txt',
        topic: 'Location Optimization Scope',
        coverage: 'explicit',
        whatAreWeLearning: 'Differentiate On-Page SEO strategies for Single-Location websites versus Multi-Location expansion websites.',
        instructorExplanation: 'The instructor highlights two core local business models:\n1. Single-Location Sites: Target 1 primary city (e.g. Sydney). Every service page incorporates the primary city into titles, headings, and body content (e.g. Homepage: "Digital Marketing Sydney", Web Design page: "Web Design Company Sydney", Web Development: "Web Development Sydney", Graphic Design: "Graphic Design Services in Sydney").\n2. Multi-Location Sites: Target a primary city plus surrounding expansion cities (e.g., Adelaide, Perth, Melbourne). When expanding, create dedicated location pages for each target city to rank effectively in those local markets.',
        instructorExamples: [
          'Single-location target: Sydney (Home, Web Design Sydney, Web Dev Sydney, Graphic Design Sydney)',
          'Multi-location target: Sydney (main), Perth, Adelaide, Melbourne expansion pages'
        ],
        simpleExplanation: 'Single-location sites optimize every service page for one city; multi-location sites build location pages for each expansion city.',
        comprehensionCheck: {
          id: 'check-4-2',
          question: 'On a single-location site targeting Sydney, how should service pages be optimized according to the instructor?',
          options: [
            { label: 'Optimize each service page for Sydney (e.g., "Web Design Company Sydney")', value: 'a', isCorrect: true },
            { label: 'Create 50 location pages for cities where the client has no business presence', value: 'b', isCorrect: false },
            { label: 'Omit location names from all service pages completely', value: 'c', isCorrect: false }
          ],
          explanation: 'Single-location businesses incorporate their target city directly into each service page\'s optimization.'
        }
      },
      {
        id: 'lesson-4-3',
        moduleId: 'module-4-onpage-seo',
        lessonNumber: 3,
        title: 'Multi-Service Location Page & Search Volume Content Structuring',
        lecture: 5,
        sourceFile: 'lec5-On Page SEO Implementation.txt',
        topic: 'Heading Hierarchy & Scaled Content',
        coverage: 'explicit',
        whatAreWeLearning: 'Master how to structure a multi-service location page using H1, H2, and proportional content word counts based on search volume.',
        instructorExplanation: 'When creating a single location page (e.g., Perth) for a business offering 5 services across multiple locations:\n• Primary Service (Highest Search Volume & Competition, e.g. "Web Design Perth", SV = 500): Assign as the H1 heading ("Web Design Perth") and write 300–400 (or 400–500) words of rich content. Include keyword in H1 and twice in body text.\n• Secondary Service (Medium Search Volume, e.g. "Web Development Perth", SV = 300): Assign as H2 heading ("Web Development Perth") and write 250–300 words. Include keyword in H2 and 1–2 times in content.\n• Tertiary Service (Lowest Volume, e.g. "Graphic Design Perth", SV = 200): Assign as H2 heading ("Graphic Design Perth") and write 200–300 words. Include keyword in H2 and once in content.\nThis concentrates local authority onto a comprehensive location page without diluting site structure with hundreds of thin pages.',
        instructorExamples: [
          'H1: Web Design Perth (SV: 500, 300–400 words, keyword in H1 + 2x in body)',
          'H2: Web Development Perth (SV: 300, 250–300 words, keyword in H2 + 1–2x in body)',
          'H2: Graphic Design Perth (SV: 200, 200–300 words, keyword in H2 + 1x in body)'
        ],
        simpleExplanation: 'Assign H1 to the service with the highest search volume, H2 to secondary services, and scale word count proportionally with search volume.',
        comprehensionCheck: {
          id: 'check-4-3',
          question: 'How does the instructor determine which service gets the H1 heading on a multi-service location page?',
          options: [
            { label: 'The service with the highest search volume and competition gets the H1 heading', value: 'a', isCorrect: true },
            { label: 'The service with the shortest title gets the H1 heading', value: 'b', isCorrect: false },
            { label: 'All services on the location page are assigned H1 headings', value: 'c', isCorrect: false }
          ],
          explanation: 'The highest search volume keyword is designated as the H1 heading, while secondary services receive H2 headings.'
        }
      },
      {
        id: 'lesson-4-4',
        moduleId: 'module-4-onpage-seo',
        lessonNumber: 4,
        title: 'Permalinks, Clean URLs & 301 Redirection Rules',
        lecture: 5,
        sourceFile: 'lec5-On Page SEO Implementation.txt',
        topic: 'URL Structure & 301 Redirects',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn permalink optimization practices and how to execute 301 redirects when updating existing URLs.',
        instructorExplanation: 'Clean permalinks (e.g. website.com/perth or website.com/web-design-perth) provide SEO clarity. However, if a client already has existing location pages ranking well, do NOT alter permalinks unnecessarily. While keywords in URLs are a ranking factor, they are not the only factor. If permalinks are extremely long or poorly structured and MUST be updated, create clean permalinks and ALWAYS install 301 permanent redirects from old URLs to new URLs using Yoast Advanced Settings, Simple 301 Redirects, or Really Simple SSL plugins to transfer ranking authority and avoid 404 errors.',
        instructorExamples: [
          'Clean permalink format: website.com/perth or website.com/web-design-perth',
          'Do not modify existing permalinks if the site is already ranking well',
          'Mandatory 301 Redirect setup via Yoast Advanced Settings or Simple 301 Redirects plugin when changing URLs'
        ],
        simpleExplanation: 'Keep URLs clean; if changing existing URLs, always set up 301 redirects to preserve rankings.',
        comprehensionCheck: {
          id: 'check-4-4',
          question: 'What critical action MUST be taken if you change an existing page\'s permalink/URL?',
          options: [
            { label: 'Set up a 301 permanent redirect from the old URL to the new URL', value: 'a', isCorrect: true },
            { label: 'Delete the website from Google Search Console', value: 'b', isCorrect: false },
            { label: 'Change the business domain name', value: 'c', isCorrect: false }
          ],
          explanation: 'A 301 redirect passes authority and ensures search engines and users are seamlessly directed to the new URL without 404 errors.'
        }
      },
      {
        id: 'lesson-4-5',
        moduleId: 'module-4-onpage-seo',
        lessonNumber: 5,
        title: 'NAP Consistency, Google Business Profile & Local Trust',
        lecture: 5,
        sourceFile: 'lec5-On Page SEO Implementation.txt',
        topic: 'NAP (Name, Address, Phone) & Trust Signals',
        coverage: 'explicit',
        whatAreWeLearning: 'Master NAP consistency rules across websites, Google Business Profile (GMB), and local citations to establish Google trust.',
        instructorExplanation: 'Local SEO relies heavily on search engine trust. Google ranks local businesses based on confidence that they are real, physical, local, and reliable.\n• NAP = Name, Address, Phone Number.\n• Name: Must be 100% EXACTLY identical everywhere — across GMB, website footer, social media, and local citations. Not even a single comma difference! Variations (such as adding "LTD" in one place and omitted elsewhere) confuse Google, degrade trust, and risk GMB profile suspension.\n• Address: Full Street, City, State, and exact Zip Code (Postal Code). The Zip Code pinpoints the exact physical location for Google trust.\n• Phone Number: Local telephone number with regional area code.\n• Local Platforms: GMB (Google Business Profile), YellowPages, Yelp, NGs List, and local citation directories.',
        instructorExamples: [
          'Exact match business Name across GMB, website, and local directories',
          'Full Address with Postal Zip Code (e.g. 66000 for Multan) to pinpoint location',
          'Local phone number with regional area code'
        ],
        simpleExplanation: 'NAP consistency builds Google trust; your Name, Address, and Phone number must be 100% identical everywhere.',
        comprehensionCheck: {
          id: 'check-4-5',
          question: 'Why must the business Name in NAP be 100% identical everywhere without even a comma difference?',
          options: [
            { label: 'Inconsistent business names confuse search engines, reduce trust scores, and risk GMB suspensions', value: 'a', isCorrect: true },
            { label: 'Search engines automatically fine businesses for punctuation differences', value: 'b', isCorrect: false },
            { label: 'WordPress prevents saving pages if the business name changes', value: 'c', isCorrect: false }
          ],
          explanation: 'Exact NAP matching across GMB and directories verifies business authenticity and builds Google trust.'
        }
      }
    ]
  },
  {
    id: 'module-5-content-planning',
    name: 'Module 5: Content Planning & Competitor Heading Synthesis',
    type: 'learning',
    version: '1.0',
    description: 'Master competitor heading extraction, 5-column gap analysis, customer pain points vs. business USPs, SEO Title vs. Master H1 formulation, and comprehensive value-add outline generation based on Lecture 6.',
    lectureSources: ['lec6-Content Planning & Competitor Headings.txt'],
    lessons: [
      {
        id: 'lesson-5-1',
        moduleId: 'module-5-content-planning',
        lessonNumber: 1,
        title: 'The Value-Add Content Philosophy & User Intent',
        lecture: 6,
        sourceFile: 'lec6-Content Planning & Competitor Headings.txt',
        topic: 'User Intent & Comprehensive Value Addition',
        coverage: 'explicit',
        whatAreWeLearning: 'Understand how a new website with zero authority can outrank established competitors by fulfilling Google\'s primary goal: satisfying User Intent with comprehensive depth.',
        instructorExplanation: 'The instructor explains that nothing on the World Wide Web is completely unwritten. You cannot write "from scratch" thinking there is zero information online. Every topic has already been written about extensively. How does a brand-new website beat established competitors with thousands of backlinks? By understanding what search engines want: User Intent. Google wants to send users to a page that answers all their questions, confusions, and issues in one place. Competitors usually only cover pieces of a topic individually. Our strategy is to collect all competitor data, synthesize it, and create one comprehensive piece with massive Value Addition so users never need to visit another website.',
        instructorExamples: [
          'Competitor A only covers causes of water damage',
          'Competitor B only covers restoration equipment',
          'Competitor C only covers the cleanup process',
          'Our Master Strategy: Combine causes, equipment, process, costs, and FAQs in one ultimate guide so the visitor never bounces'
        ],
        simpleExplanation: 'Beat high-authority competitors by writing one comprehensive piece that answers every visitor question so they never leave your site.',
        comprehensionCheck: {
          id: 'check-5-1',
          question: 'According to the instructor, how can a new website outrank older, high-authority competitors?',
          options: [
            { label: 'By satisfying user intent so comprehensively that visitors never need to visit multiple competing sites', value: 'a', isCorrect: true },
            { label: 'By buying 10,000 spam backlinks on Fiverr', value: 'b', isCorrect: false },
            { label: 'By writing 100-word short articles to load faster', value: 'c', isCorrect: false }
          ],
          explanation: 'Google prioritizes User Intent and comprehensive topical depth that answers all user questions in one place.'
        }
      },
      {
        id: 'lesson-5-2',
        moduleId: 'module-5-content-planning',
        lessonNumber: 2,
        title: 'Top 5 Competitors SERP Extraction & 5-Column Matrix',
        lecture: 6,
        sourceFile: 'lec6-Content Planning & Competitor Headings.txt',
        topic: 'Competitor Heading Extraction & Gap Analysis',
        coverage: 'explicit',
        whatAreWeLearning: 'Learn how to inspect the top 5 ranking competitors on Google SERP and compare their H1, H2, and subheadings in a side-by-side matrix.',
        instructorExplanation: 'In the live demonstration, the instructor searches for "mold removal los angeles", opens the top 5 organic competitors, and copies their headings into 5 parallel columns in a spreadsheet. By placing their headings side by side, you immediately see what each competitor covers, what they have in common, and where their content gaps lie. Most competitors only cover basic steps or domain-authority pages with thin content. Seeing the full matrix allows you to exploit their missing sections and build a superior outline.',
        instructorExamples: [
          'Column 1 (Competitor 1): "How Process Works", "Air Filtration", "Water Repair"',
          'Column 2 (Competitor 2): "About Our Company", "How Mold Grows", "Contact Us"',
          'Column 3 (Competitor 3): "Toxic Mold Signs", "Inspection & Testing", "Cost"',
          'Column 4 & 5: Commercial vs. Residential cleanup, Flood recovery',
          'Gap Discovered: None of the 5 competitors have a dedicated "Why Choose Us" or "Before & After" proof section!'
        ],
        simpleExplanation: 'Paste top 5 competitors\' headings side-by-side in a spreadsheet matrix to spot what they missed and exploit their content gaps.',
        comprehensionCheck: {
          id: 'check-5-2',
          question: 'What is the main objective of creating a 5-column competitor headings matrix?',
          options: [
            { label: 'To identify common topics and exploit glaring content gaps that competitors missed', value: 'a', isCorrect: true },
            { label: 'To copy and paste their exact text word-for-word', value: 'b', isCorrect: false },
            { label: 'To calculate how much hosting competitors pay', value: 'c', isCorrect: false }
          ],
          explanation: 'Comparing competitor headings side-by-side reveals content gaps that you can fulfill to deliver superior value.'
        }
      },
      {
        id: 'lesson-5-3',
        moduleId: 'module-5-content-planning',
        lessonNumber: 3,
        title: 'Customer Pain Points vs. Business USPs',
        lecture: 6,
        sourceFile: 'lec6-Content Planning & Competitor Headings.txt',
        topic: 'Visitor Psychology, Pain Points & USPs',
        coverage: 'explicit',
        whatAreWeLearning: 'Identify the emotional and practical pain points of prospective customers and counter each pain with a concrete Unique Selling Point (USP).',
        instructorExplanation: 'The instructor explains that high-converting content addresses the consumer\'s pain points. When someone has water damage or mold in their home, what are they thinking? What are their fears?\n1. Emergency panic: Water is actively leaking; need someone to arrive fast.\n2. Health risks: Fear that toxic black mold will make their kids or pets sick.\n3. Fear of bad contractors: Worry that an unlicensed handyman will steal, overcharge, or worsen the problem.\n4. Cost & insurance stress: Fear of huge surprise bills and insurance claim denials.\nWe counter each pain with specific USPs: 24/7 Rapid 30-Minute Response, Licensed & IICRC Certified technicians, 100% Satisfaction Guarantee, and Direct Insurance Billing.',
        instructorExamples: [
          'Pain: "My kids might get sick from toxic mold" -> USP: Hospital-Grade HEPA filtration & eco-friendly safe sanitization',
          'Pain: "Water is dripping on my floor right now" -> USP: 24/7 Rapid Emergency Response (on-site in 30 minutes)',
          'Pain: "Contractor will overcharge or steal" -> USP: State-Licensed, Background-Checked, 100% Satisfaction Guarantee'
        ],
        simpleExplanation: 'Map out customer fears (emergency, health risks, bad contractors) and resolve them with strong USPs (24/7 arrival, licenses, guarantees).',
        comprehensionCheck: {
          id: 'check-5-3',
          question: 'Why does the instructor insist on identifying customer pain points before writing content?',
          options: [
            { label: 'Because addressing customer fears and providing solutions builds trust, increases engagement, and drives phone calls', value: 'a', isCorrect: true },
            { label: 'Because Google Search Console requires a list of pain points to index pages', value: 'b', isCorrect: false },
            { label: 'To scare visitors into leaving the website', value: 'c', isCorrect: false }
          ],
          explanation: 'Directly addressing customer pain points and providing USPs reassures the visitor and turns panic into conversions.'
        }
      },
      {
        id: 'lesson-5-4',
        moduleId: 'module-5-content-planning',
        lessonNumber: 4,
        title: 'The Golden Law: SEO Title vs. Master H1 Heading',
        lecture: 6,
        sourceFile: 'lec6-Content Planning & Competitor Headings.txt',
        topic: 'SEO Title (CTR) vs. H1 (Retention & Trust)',
        coverage: 'explicit',
        whatAreWeLearning: 'Distinguish between the purpose of the SERP SEO Title and the On-Page Master H1, and learn how to formulate an H1 that retains visitors.',
        instructorExplanation: 'The instructor teaches a crucial distinction in SEO copywriting:\n• SEO Title: Designed specifically to get the CLICK in Google search results (Click-Through Rate / CTR). It includes the keyword, hook, and brand.\n• H1 Heading: Designed to RETAIN the visitor on the page once they land. When a user clicks and arrives on your site, they need immediate reassurance that they are in the right place, that you are trustworthy, and that you solve their pain immediately.\nWe formulate our Master H1 using: [Trust Modifier] + [Primary Target Keyword] + [Core USP / Pain-Point Resolution].\nExample: "#1 Licensed Mold Removal Los Angeles Company — 24/7 Rapid Emergency Response".',
        instructorExamples: [
          'SEO Title: "Best Mold Removal Los Angeles | #1 Licensed Remediation Experts (24/7)"',
          'Master H1: "#1 Licensed Mold Removal Los Angeles Company — 24/7 Rapid Emergency Response"',
          'Formula: Trust Adjective (Licensed/Certified) + Keyword + USP'
        ],
        simpleExplanation: 'The SEO Title gets the click in Google; the Master H1 retains the visitor on the page by proving trust and solving their emergency.',
        comprehensionCheck: {
          id: 'check-5-4',
          question: 'What is the fundamental difference between the SEO Title and the H1 Heading according to the instructor?',
          options: [
            { label: 'The SEO Title is designed to win the click in SERPs; the H1 Heading is designed to retain the visitor on page with trust and USPs', value: 'a', isCorrect: true },
            { label: 'The SEO Title and H1 must always be 100% identical character-for-character', value: 'b', isCorrect: false },
            { label: 'The H1 is only visible to search engines and hidden from users', value: 'c', isCorrect: false }
          ],
          explanation: 'SEO Titles drive clicks from Google; H1 headings reassure and retain visitors once they land.'
        }
      },
      {
        id: 'lesson-5-5',
        moduleId: 'module-5-content-planning',
        lessonNumber: 5,
        title: 'Value-Add Master Outline: "Why Choose Us", Proof & GMB Services',
        lecture: 6,
        sourceFile: 'lec6-Content Planning & Competitor Headings.txt',
        topic: 'Synthesized Master Outline & GMB Services Spectrum',
        coverage: 'explicit',
        whatAreWeLearning: 'Synthesize the final, comprehensive content blueprint including "Why Choose Us", "Before & After" proof, full GMB services spectrum, and FAQs.',
        instructorExplanation: 'In the final part of Lecture 6, the instructor constructs the master outline that beats all competitors:\n1. H1: Retention-first title with trust + USP.\n2. "Why We Are The Go-To Company in Los Angeles" (Why Choose Us): 4 sub-points (Emergency arrival, local LA neighbors, licensed technicians, satisfaction guarantee). None of the competitors had this!\n3. "Before & After: Have a Look at Our Work": Visual proof that builds human trust and proves to Google that this is a real physical service company, not an affiliate spam site.\n4. Comprehensive Services Spectrum: Competitors list only 2-3 services. We inspect competitor GMB profiles and include the full spectrum (toxic black mold, attic/crawlspace, HEPA containment, drying, structural reconstruction).\n5. Step-by-step restoration process.\n6. FAQs addressing costs, insurance, and landlord disputes.',
        instructorExamples: [
          'H2: Why We Are The Go-To Mold Removal Company in Los Angeles',
          'H2: Before & After: Real Los Angeles Transformations',
          'H2: Comprehensive Mold Remediation Services We Provide (Toxic, Attic, Basement, HEPA, Water Drying)',
          'H2: Step-by-Step Restoration Process & FAQs'
        ],
        simpleExplanation: 'Build a master outline featuring "Why Choose Us", "Before/After" proof, complete service spectrum from GMBs, and FAQs to dominate competitors.',
        comprehensionCheck: {
          id: 'check-5-5',
          question: 'Why does the instructor include a "Before & After" case studies section in the master outline?',
          options: [
            { label: 'It establishes human trust with visitors and demonstrates to Google that the site represents a legitimate, real-world service business', value: 'a', isCorrect: true },
            { label: 'It fills page length without needing any written words', value: 'b', isCorrect: false },
            { label: 'Search engines only rank websites that have image sliders', value: 'c', isCorrect: false }
          ],
          explanation: 'Before/After case studies prove physical competence to prospective clients and authentic local presence to search engines.'
        }
      }
    ]
  }
];

